import { useState, useEffect, useCallback } from 'react';
import { useAuth } from 'contexts/AuthContext';
import { AsignacionItem, MatriculaResponse } from '../types/matricula.types';
import { PeriodoResponse } from '../types/periodo.types';
import {
  NotaActividadResponse,
  NotaActividadPayload,
  NotaMasivoItem,
  esNivelLetra,
} from '../types/notaActividad.types';
import { asignacionService } from '../services/matriculaService';
import { periodoService } from '../services/periodoService';
import { notaActividadService, matriculaListService } from '../services/notaActividadService';
import { exoneracionService } from '../services/exoneracionService';

// ============================================================
// useCalificaciones – orquesta el flujo completo del docente
//   1. Seleccionar curso asignado
//   2. Seleccionar periodo
//   3. Ver estudiantes y registrar notas
// ============================================================

export const useCalificaciones = () => {
  const { user } = useAuth();

  // ── Paso 1: cursos asignados ──────────────────────────────
  const [asignaciones, setAsignaciones]         = useState<AsignacionItem[]>([]);
  const [loadingAsignaciones, setLoadingAsig]   = useState(true);

  // ── Paso 2: selección de curso y periodo ──────────────────
  const [selectedAsignacion, setSelectedAsignacion] = useState<AsignacionItem | null>(null);
  const [periodos, setPeriodos]                     = useState<PeriodoResponse[]>([]);
  const [selectedPeriodoId, setSelectedPeriodoId]   = useState<number | null>(null);
  const [loadingPeriodos, setLoadingPeriodos]       = useState(false);

  // ── Paso 3: estudiantes y notas ───────────────────────────
  const [estudiantes, setEstudiantes]       = useState<MatriculaResponse[]>([]);
  const [notas, setNotas]                   = useState<NotaActividadResponse[]>([]);
  const [loadingNotas, setLoadingNotas]     = useState(false);

  // ── Registro de nota ──────────────────────────────────────
  const [submitting, setSubmitting]         = useState(false);
  const [submitError, setSubmitError]       = useState<string | null>(null);
  const [exoneradosOmitidos, setExoneradosOmitidos] = useState<string[]>([]);

  // ── Error general ─────────────────────────────────────────
  const [error, setError] = useState<string | null>(null);

  // ── 1. Cargar asignaciones del docente al montar ──────────
  useEffect(() => {
    if (!user) return;
    const load = async () => {
      setLoadingAsig(true); setError(null);
      try {
        const data = await asignacionService.getByDocente(user.idUsuario);
        setAsignaciones(data.filter(a => a.activo));
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : 'Error al cargar asignaciones');
      } finally { setLoadingAsig(false); }
    };
    load();
  }, [user]);

  // ── 2. Al seleccionar un curso → cargar periodos ──────────
  const selectAsignacion = useCallback(async (asignacion: AsignacionItem) => {
    setSelectedAsignacion(asignacion);
    setSelectedPeriodoId(null);
    setEstudiantes([]);
    setNotas([]);
    setLoadingPeriodos(true);
    try {
      const data = await periodoService.listarPorAnioEscolar(asignacion.anioEscolarId);
      setPeriodos(data);
    } catch {
      setPeriodos([]);
    } finally { setLoadingPeriodos(false); }
  }, []);

  // ── 3. Al seleccionar periodo → cargar estudiantes y notas ─
  const selectPeriodo = useCallback(async (periodoId: number) => {
    if (!selectedAsignacion) return;
    setSelectedPeriodoId(periodoId);
    setLoadingNotas(true); setError(null);
    try {
      const [estudiantesData, notasData] = await Promise.all([
        matriculaListService.listarPorGradoSeccionAnio(
          selectedAsignacion.gradoId,
          selectedAsignacion.seccionId,
          selectedAsignacion.anioEscolarId,
        ),
        notaActividadService.listarPorAsignacionYPeriodo(
          selectedAsignacion.idAsignacion,
          periodoId,
        ),
      ]);
      setEstudiantes(estudiantesData);
      setNotas(notasData);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error al cargar datos');
    } finally { setLoadingNotas(false); }
  }, [selectedAsignacion]);

  // ── Registrar actividad masiva (toda la sección) ──────────
  const registrarMasivo = useCallback(async (
    descripcion: string,
    notasInput: NotaMasivoItem[],
  ) => {
    if (!selectedAsignacion || !selectedPeriodoId) return;
    setSubmitting(true); setSubmitError(null); setExoneradosOmitidos([]);

    // Filtrar estudiantes con exoneración TOTAL antes de enviar
    // GET /exoneraciones?cursoId=X&anioEscolarId=Y
    let notasFiltradas = notasInput;
    try {
      const exoneraciones = await exoneracionService.listar({
        cursoId:       selectedAsignacion.cursoId,
        anioEscolarId: selectedAsignacion.anioEscolarId,
      });
      const matriculasExoneradas = new Set(
        exoneraciones
          .filter(ex => ex.activo && ex.tipo === 'TOTAL')
          .map(ex => ex.matriculaId)
      );
      if (matriculasExoneradas.size > 0) {
        notasFiltradas = notasInput.filter(n => !matriculasExoneradas.has(n.matriculaId));
        const nombres = estudiantes
          .filter(e => matriculasExoneradas.has(e.idMatricula))
          .map(e => `${e.estudianteApellidos}, ${e.estudianteNombres}`);
        setExoneradosOmitidos(nombres);
      }
    } catch {
      // Si falla la consulta de exoneraciones, continúa con todos (el backend cortará si hay problema)
    }

    if (notasFiltradas.length === 0) {
      setSubmitting(false);
      return; // todos estaban exonerados, nada que enviar
    }

    try {
      const nuevas = await notaActividadService.crearMasivo({
        asignacionAcademicaId: selectedAsignacion.idAsignacion,
        periodoId:             selectedPeriodoId,
        descripcion,
        notas:                 notasFiltradas,
      });
      setNotas(prev => [...prev, ...nuevas]);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Error al registrar las notas';
      const esExoneracion = msg.includes('exoneración TOTAL');
      setSubmitError(
        esExoneracion
          ? `⚠️ ${msg} No se puede registrar ninguna nota en este curso.`
          : msg
      );
      throw e;
    } finally { setSubmitting(false); }
  }, [selectedAsignacion, selectedPeriodoId, estudiantes]);

  // ── Corregir nota existente (PUT) ────────────────────────
  const editarNota = useCallback(async (
    notaExistente: NotaActividadResponse,
    nuevoValor: number | string,
  ) => {
    setSubmitting(true); setSubmitError(null);
    try {
      const usaLetra = typeof nuevoValor === 'string';
      const payload: NotaActividadPayload = {
        matriculaId:           notaExistente.matriculaId,
        asignacionAcademicaId: notaExistente.asignacionAcademicaId,
        periodoId:             notaExistente.periodoId,
        descripcion:           notaExistente.descripcion,
        observacion:           notaExistente.observacion ?? '',
        ...(usaLetra
          ? { notaLetra: nuevoValor as string }
          : { nota: nuevoValor as number }),
      };
      const actualizada = await notaActividadService.modificar(notaExistente.idNotaActividad, payload);
      setNotas(prev => prev.map(n =>
        n.idNotaActividad === notaExistente.idNotaActividad ? actualizada : n
      ));
    } catch (e: unknown) {
      setSubmitError(e instanceof Error ? e.message : 'Error al actualizar la nota');
      throw e;
    } finally { setSubmitting(false); }
  }, []);

  // ── Subsanar nota (POST individual — registro tardío) ─────
  const subsanarNota = useCallback(async (
    matriculaId: number,
    descripcion: string,
    nuevoValor: number | string,
  ) => {
    if (!selectedAsignacion || !selectedPeriodoId) return;
    setSubmitting(true); setSubmitError(null);
    try {
      const usaLetra = typeof nuevoValor === 'string';
      const payload: NotaActividadPayload = {
        matriculaId,
        asignacionAcademicaId: selectedAsignacion.idAsignacion,
        periodoId:             selectedPeriodoId,
        descripcion,
        observacion:           '',
        ...(usaLetra
          ? { notaLetra: nuevoValor as string }
          : { nota: nuevoValor as number }),
      };
      const nueva = await notaActividadService.crear(payload);
      setNotas(prev => [...prev, nueva]);
    } catch (e: unknown) {
      setSubmitError(e instanceof Error ? e.message : 'Error al registrar la nota');
      throw e;
    } finally { setSubmitting(false); }
  }, [selectedAsignacion, selectedPeriodoId]);

  // ── Eliminar una nota ─────────────────────────────────────
  const eliminarNota = useCallback(async (idNotaActividad: number) => {
    try {
      await notaActividadService.eliminar(idNotaActividad);
      setNotas(prev => prev.filter(n => n.idNotaActividad !== idNotaActividad));
    } catch (e: unknown) {
      setSubmitError(e instanceof Error ? e.message : 'Error al eliminar la nota');
    }
  }, []);

  // ── Volver al listado de cursos ───────────────────────────
  const volver = useCallback(() => {
    setSelectedAsignacion(null);
    setSelectedPeriodoId(null);
    setPeriodos([]);
    setEstudiantes([]);
    setNotas([]);
    setError(null);
    setSubmitError(null);
  }, []);

  return {
    // Estado
    asignaciones, loadingAsignaciones,
    selectedAsignacion, periodos, selectedPeriodoId, loadingPeriodos,
    estudiantes, notas, loadingNotas,
    submitting, submitError, error, exoneradosOmitidos,
    // Acciones
    selectAsignacion, selectPeriodo,
    registrarMasivo, editarNota, subsanarNota, eliminarNota, volver,
  };
};
